// Blocking policy editor — the contact form's "Blocking policy" section.
//
// Each axis renders as its own outlined card (or, given `axis`, that one axis
// bare, inside a record section) with three optional sub-sections separated by
// full-width dividers:
//
//   Compliance ─ rule chips (or "No issues") + "Ignore compliance block"
//                checkbox. When checked: inline duration Select + reason field.
//   Debt (TIMECARDS only) ─ "No issues" + "Ignore debt blocking"
//                checkbox. When checked: duration Select + reason field.
//   Manual (CASES, TIMECARDS only) ─ tinted background; "Add manual block"
//                checkbox. When checked: reason field. No duration.
//
// Edits flow into the parent contact form's `blocking.*` subtree via
// useFormIntents, which buildBlocksPayload turns into the wire payload at save
// time. A proactive compliance exemption (no row yet) is persisted too: at save
// time buildBlocksPayload emits a no-id COMPLIANCE write, which the BE accepts as
// a proactive override (BlockingReconciliationService.applyDesiredBlocks).

import { PersonOutline as PersonOutlineIcon } from 'components/icons';
import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import { DatePickerField } from 'components/forms/inputs/common/DatePickerField';
import ChangeReasonInput from 'components/forms/inputs/rhf-inputs/ChangeReasonInput';
import { Checkbox } from 'components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Separator } from 'components/ui/separator';
import { format, isValid } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { Fragment, useId, useState } from 'react';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import { ComplianceBlockActiveRuleDto, ComplianceBlockingSeverity } from 'types/compliance';
import { BlockedActionTypeCode, ContactDto, ContactForm, OverrideFormState } from 'types/contacts';
import { t } from 'utils/getTranslations';
import {
    axisStatusChip,
    axisStatusFromRows,
    complianceExemptKey,
    complianceRulesForAxis,
    effectiveManualEnabled,
    EffectiveRow,
    effectiveRowFor,
    IntentMap,
    useFormIntents,
} from './BlockingPolicyEditorShared';
import { parseLocalDate } from './BlockingPolicyReadonlyShared';

interface Props {
    contact?: ContactDto;
    /** One axis alone, as a record section's fields: the section draws the card and its title. */
    axis?: BlockedActionTypeCode;
}

const AXIS_TITLE: Record<BlockedActionTypeCode, string> = {
    CASES: 'Block adding new cases',
    TIMECARDS: 'Block adding new timecards/expenses',
    AML_TIMECARDS: 'Block adding new timecards for AML cases',
};

const severityChipType: Record<ComplianceBlockingSeverity, ChipType> = {
    [ComplianceBlockingSeverity.CRITICAL]: 'critical',
    [ComplianceBlockingSeverity.HIGH]: 'error',
    [ComplianceBlockingSeverity.MEDIUM]: 'warning',
    [ComplianceBlockingSeverity.LOW]: 'info',
};

// blockedFrom is the first day the row resumes enforcing; the UI shows the
// inclusive last day the ignore covers (blockedFrom - 1). The two helpers are
// inverses so the date-picker round-trip matches.
const blockedFromToDisplayDate = (blockedFrom: string | null): Date | null => {
    if (!blockedFrom) return null;
    const parsed = parseLocalDate(blockedFrom);
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() - 1);
};

const displayDateToBlockedFrom = (date: Date): string => {
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
    return format(next, 'yyyy-MM-dd');
};

const BlockingPolicyEditor: React.FC<Props> = ({ contact, axis }) => {
    if (!contact) return null;
    return <BlockingPolicyEditorInner contact={contact} axis={axis} />;
};

const BlockingPolicyEditorInner: React.FC<{ contact: ContactDto; axis?: BlockedActionTypeCode }> = ({
    contact,
    axis,
}) => {
    const { intents, setManual, setOverride } = useFormIntents(contact);
    if (axis) {
        return (
            <AxisCard
                contact={contact}
                axis={axis}
                intents={intents}
                setManual={setManual}
                setOverride={setOverride}
                bare
            />
        );
    }
    return (
        <div className="flex flex-col gap-4">
            {(['CASES', 'TIMECARDS', 'AML_TIMECARDS'] as const).map((axis, index) => (
                <Fragment key={axis}>
                    {index > 0 && <Separator />}
                    <AxisCard
                        contact={contact}
                        axis={axis}
                        intents={intents}
                        setManual={setManual}
                        setOverride={setOverride}
                    />
                </Fragment>
            ))}
        </div>
    );
};

interface AxisCardProps {
    contact: ContactDto;
    axis: BlockedActionTypeCode;
    intents: IntentMap;
    setManual: ReturnType<typeof useFormIntents>['setManual'];
    setOverride: ReturnType<typeof useFormIntents>['setOverride'];
    /** Without its own card and title, the status first; its rows reach the section's edges. */
    bare?: boolean;
}

const AxisCard: React.FC<AxisCardProps> = ({ contact, axis, intents, setManual, setOverride, bare }) => {
    const manualAxis = axis === 'AML_TIMECARDS' ? null : (axis as 'CASES' | 'TIMECARDS');
    const debtApplies = axis === 'TIMECARDS';

    const compliance = effectiveRowFor(contact, 'COMPLIANCE', axis, intents);
    const debt = debtApplies ? effectiveRowFor(contact, 'DEBT', axis, intents) : null;
    const manual = manualAxis ? effectiveManualEnabled(contact, manualAxis, intents) : null;
    const status = axisStatusFromRows(
        [compliance, ...(debt ? [debt] : [])].filter((r) => r.present),
        manual?.enabled ?? false
    );
    const chip = axisStatusChip(status);
    const manualHasServerRow = !!manual?.row && !manual.row.isIgnored;
    const statusChip = (
        <StatusChip
            label={chip.label}
            type={chip.type}
            className="new:leading-[1.0769] min-w-[90px] px-[18px] py-2.5 text-[13px] leading-5 font-medium"
        />
    );
    const rows = (
        <>
            <ComplianceSubSection contact={contact} axis={axis} compliance={compliance} setOverride={setOverride} />
            {debtApplies && debt && (
                <>
                    <Separator className="mr-[120px] ml-4 data-horizontal:w-auto" />
                    <DebtSubSection debt={debt} setOverride={setOverride} />
                </>
            )}
            {manualAxis && (
                <ManualSubSection
                    axis={manualAxis}
                    hasServerRow={manualHasServerRow}
                    manualEnabled={manual?.enabled ?? false}
                    userEdited={manual?.intent !== undefined}
                    setManual={setManual}
                />
            )}
        </>
    );

    if (bare) {
        // The section's fields grid zeroes its children's margins, so the bleed sits one level down: each row spans
        // the section's padding and takes it back as its own, so the manual panel's tint reaches the frame.
        return (
            <div>
                <div className="-mx-[19px] flex flex-col [&>div]:px-[19px]">
                    <div className="pb-1">{statusChip}</div>
                    {rows}
                </div>
            </div>
        );
    }
    return (
        <section className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden relative overflow-hidden rounded-[10px] border">
            <div className="absolute top-4 right-4">{statusChip}</div>
            <div className="px-4 pt-4 pb-3">
                <p className="text-body1 font-semibold">{AXIS_TITLE[axis]}</p>
            </div>
            {rows}
        </section>
    );
};

// ─── Compliance ────────────────────────────────────────────────────────────────

interface ComplianceSubSectionProps {
    contact: ContactDto;
    axis: BlockedActionTypeCode;
    compliance: EffectiveRow;
    setOverride: ReturnType<typeof useFormIntents>['setOverride'];
}

const ComplianceSubSection: React.FC<ComplianceSubSectionProps> = ({ contact, axis, compliance, setOverride }) => {
    const rules = complianceRulesForAxis(contact, axis);
    const hasRules = rules.length > 0;
    const { control, setValue, getValues } = useFormContext<ContactForm>();
    const exemptKey = complianceExemptKey(axis);
    const exempt = useWatch({ control, name: `blocking.${exemptKey}` as Path<ContactForm> }) as
        | OverrideFormState
        | undefined;

    if (!compliance.present) {
        const ignored = !!exempt && (exempt.isIgnored || !!exempt.blockedFrom);
        const writeExempt = (next: { isIgnored: boolean; blockedFrom: string | null }) => {
            setValue(`blocking.${exemptKey}.isIgnored` as Path<ContactForm>, next.isIgnored, { shouldDirty: true });
            setValue(`blocking.${exemptKey}.blockedFrom` as Path<ContactForm>, next.blockedFrom, {
                shouldDirty: true,
            });
        };
        return (
            <SubSectionBody
                label="Detected compliance rules"
                summary={<MutedText>No issues</MutedText>}
                checkboxLabel="Ignore compliance block"
                ignored={ignored}
                userEdited={ignored}
                isIgnored={exempt?.isIgnored ?? true}
                blockedFrom={exempt?.blockedFrom ?? null}
                showDuration
                reasonPath={`blocking.${exemptKey}.changeReason` as Path<ContactForm>}
                onToggle={(next) => {
                    writeExempt({ isIgnored: next, blockedFrom: null });
                    if (!next) {
                        setValue(`blocking.${exemptKey}.changeReason` as Path<ContactForm>, '', {
                            shouldDirty: true,
                        });
                    }
                }}
                onDurationChange={writeExempt}
            />
        );
    }

    const ignored = compliance.isIgnored || !!compliance.blockedFrom;

    // Read the current changeReason from the form (not from compliance.intent)
    // so a typed reason survives uncheck→recheck cycles. The intent is rebuilt
    // from the isIgnored/blockedFrom diff and goes undefined when those revert
    // to the server baseline, which would otherwise wipe the typed reason.
    const reasonFieldPath = `blocking.overrides.r${compliance.row?.id}.changeReason` as Path<ContactForm>;
    const currentReason = () => (getValues(reasonFieldPath) as string | undefined) ?? '';

    const onToggle = (next: boolean) => {
        if (!compliance.row) return;
        // Always write the desired state; buildIntentsFromForm produces an
        // override intent only when it diverges from the server row. Clearing
        // the intent instead would resurrect the server's isIgnored value, so
        // unchecking a server-ignored row would silently re-check itself.
        setOverride(compliance.row.id, {
            kind: 'override',
            blockId: compliance.row.id,
            isIgnored: next,
            blockedFrom: null,
            reason: currentReason(),
        });
    };

    const onDurationChange = (next: { isIgnored: boolean; blockedFrom: string | null }) => {
        if (!compliance.row) return;
        setOverride(compliance.row.id, {
            kind: 'override',
            blockId: compliance.row.id,
            ...next,
            reason: currentReason(),
        });
    };

    return (
        <SubSectionBody
            label="Detected compliance rules"
            summary={hasRules ? <RuleChips rules={rules} /> : <MutedText>No issues</MutedText>}
            checkboxLabel="Ignore compliance block"
            ignored={ignored}
            userEdited={compliance.intent !== undefined}
            isIgnored={compliance.isIgnored}
            blockedFrom={compliance.blockedFrom}
            showDuration
            reasonPath={`blocking.overrides.r${compliance.row!.id}.changeReason` as Path<ContactForm>}
            onToggle={onToggle}
            onDurationChange={onDurationChange}
        />
    );
};

const RuleChips: React.FC<{ rules: ComplianceBlockActiveRuleDto[] }> = ({ rules }) => (
    <div className="flex flex-wrap gap-1">
        {rules.map((rule) => (
            <StatusChip
                key={rule.id}
                label={t(`compliance.blocking-rule.${rule.ruleCode}`)}
                size="small"
                type={severityChipType[rule.severity]}
            />
        ))}
    </div>
);

// ─── Debt ──────────────────────────────────────────────────────────────────────

interface DebtSubSectionProps {
    debt: EffectiveRow;
    setOverride: ReturnType<typeof useFormIntents>['setOverride'];
}

const DebtSubSection: React.FC<DebtSubSectionProps> = ({ debt, setOverride }) => {
    const { control, setValue, getValues } = useFormContext<ContactForm>();
    const exempt = useWatch({ control, name: 'blocking.debtTimecardsExempt' }) as
        | { isIgnored: boolean; blockedFrom: string | null; changeReason: string }
        | undefined;

    if (debt.present) {
        const ignored = debt.isIgnored || !!debt.blockedFrom;
        // See ComplianceSubSection: read the reason from the form so it
        // survives uncheck→recheck cycles.
        const reasonFieldPath = `blocking.overrides.r${debt.row?.id}.changeReason` as Path<ContactForm>;
        const currentReason = () => (getValues(reasonFieldPath) as string | undefined) ?? '';
        const onToggle = (next: boolean) => {
            if (!debt.row) return;
            setOverride(debt.row.id, {
                kind: 'override',
                blockId: debt.row.id,
                isIgnored: next,
                blockedFrom: null,
                reason: currentReason(),
            });
        };
        const onDurationChange = (n: { isIgnored: boolean; blockedFrom: string | null }) => {
            if (!debt.row) return;
            setOverride(debt.row.id, {
                kind: 'override',
                blockId: debt.row.id,
                ...n,
                reason: currentReason(),
            });
        };
        return (
            <SubSectionBody
                label="Debt"
                summary={<MutedText>{debt.enforcing ? 'Block applied' : 'No issues'}</MutedText>}
                checkboxLabel="Ignore debt blocking"
                ignored={ignored}
                userEdited={debt.intent !== undefined}
                isIgnored={debt.isIgnored}
                blockedFrom={debt.blockedFrom}
                showDuration
                reasonPath={`blocking.overrides.r${debt.row!.id}.changeReason` as Path<ContactForm>}
                onToggle={onToggle}
                onDurationChange={onDurationChange}
            />
        );
    }

    // No real row → proactive exemption via the form's debtTimecardsExempt slot
    // (BE accepts no-id DEBT writes), persisted on save.
    const ignored = !!exempt && (exempt.isIgnored || !!exempt.blockedFrom);
    const writeExempt = (next: { isIgnored: boolean; blockedFrom: string | null }) => {
        setValue('blocking.debtTimecardsExempt.isIgnored' as Path<ContactForm>, next.isIgnored, {
            shouldDirty: true,
        });
        setValue('blocking.debtTimecardsExempt.blockedFrom' as Path<ContactForm>, next.blockedFrom, {
            shouldDirty: true,
        });
    };

    return (
        <SubSectionBody
            label="Debt"
            summary={<MutedText>No issues</MutedText>}
            checkboxLabel="Ignore debt blocking"
            ignored={ignored}
            userEdited={ignored}
            isIgnored={exempt?.isIgnored ?? true}
            blockedFrom={exempt?.blockedFrom ?? null}
            showDuration
            reasonPath={'blocking.debtTimecardsExempt.changeReason' as Path<ContactForm>}
            onToggle={(next) => {
                writeExempt({ isIgnored: next, blockedFrom: null });
                if (!next) {
                    setValue('blocking.debtTimecardsExempt.changeReason' as Path<ContactForm>, '', {
                        shouldDirty: true,
                    });
                }
            }}
            onDurationChange={writeExempt}
        />
    );
};

// ─── Manual ────────────────────────────────────────────────────────────────────

interface ManualSubSectionProps {
    axis: 'CASES' | 'TIMECARDS';
    hasServerRow: boolean;
    manualEnabled: boolean;
    userEdited: boolean;
    setManual: ReturnType<typeof useFormIntents>['setManual'];
}

const ManualSubSection: React.FC<ManualSubSectionProps> = ({
    axis,
    hasServerRow,
    manualEnabled,
    userEdited,
    setManual,
}) => {
    const { control, getValues } = useFormContext<ContactForm>();
    const checkboxId = useId();
    const reasonPath = (
        axis === 'CASES' ? 'blocking.manualCases.changeReason' : 'blocking.manualTimecards.changeReason'
    ) as Path<ContactForm>;
    // Preserve a typed reason across toggle/un-toggle. setManual always writes
    // changeReason, so without this it would clobber the field on each click.
    const currentReason = () => (getValues(reasonPath) as string | undefined) ?? '';

    const handleToggle = (next: boolean) => {
        if (next) {
            setManual(axis, { kind: 'manual', action: 'SET', reason: currentReason() });
        } else {
            // If a server row exists, write a DELETE intent so the checkbox-off
            // persists; otherwise clear the intent so the form returns to baseline.
            setManual(axis, hasServerRow ? { kind: 'manual', action: 'DELETE', reason: currentReason() } : null);
        }
    };

    // Operator-driven block — a distinct footer panel in the brand primary green,
    // visually separate from the automatic compliance/debt sources above. Identity
    // colour is primary only; amber/red are reserved for warning/blocked. The
    // horizontal inset matches the white card content so the icon, checkbox, label
    // and input line up with the compliance rows above.
    return (
        <div className="bg-primary-surface border-primary/18 border-t px-4 pt-[18px] pb-6">
            <div className="mb-3.5 flex items-center gap-1.5">
                <PersonOutlineIcon className="text-primary text-[18px]" />
                <span className="text-body1 new:text-label text-primary new:tracking-[0.06em] font-semibold tracking-[0.02em] uppercase">
                    Manual block
                </span>
            </div>
            <div className="flex min-h-8 items-center gap-2">
                {/* The same checkbox as "Ignore compliance block", checked in the brand primary green. */}
                <Checkbox
                    id={checkboxId}
                    checked={manualEnabled}
                    onCheckedChange={(checked) => handleToggle(checked)}
                />
                <label htmlFor={checkboxId} className="text-fg-secondary text-sm">
                    Add manual block
                </label>
            </div>
            {userEdited && <ChangeReasonInput control={control} name={reasonPath} required />}
        </div>
    );
};

// ─── Sub-section body ──────────────────────────────────────────────────────────

interface SubSectionBodyProps {
    label: string;
    summary: React.ReactNode;
    checkboxLabel: string;
    ignored: boolean;
    // True when this row's form state diverges from the state it had when the
    // edit page was opened. Drives reason-input visibility — show it when the
    // user is committing a change, hide it the moment the toggle/duration
    // round-trip back to the initial state.
    userEdited: boolean;
    isIgnored: boolean;
    blockedFrom: string | null;
    showDuration: boolean;
    reasonPath: Path<ContactForm> | null;
    onToggle: (next: boolean) => void;
    onDurationChange: (next: { isIgnored: boolean; blockedFrom: string | null }) => void;
}

const SubSectionBody: React.FC<SubSectionBodyProps> = ({
    label,
    summary,
    checkboxLabel,
    ignored,
    userEdited,
    isIgnored,
    blockedFrom,
    showDuration,
    reasonPath,
    onToggle,
    onDurationChange,
}) => {
    const { control } = useFormContext<ContactForm>();
    const checkboxId = useId();
    // Mirrors DurationSelect's pendingUntilDate so the parent can keep the
    // reason input visible while the user is mid-mode-switch (no date picked
    // yet → no form diff). Clears when the user picks a date or reverts mode,
    // letting userEdited (form-vs-initial diff) take over again.
    const [durationPending, setDurationPending] = useState(false);

    return (
        <div className="flex flex-col gap-2 p-4">
            <p className="text-body2 text-fg-secondary">{label}</p>
            {summary}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex min-h-8 items-center gap-2">
                    <Checkbox
                        id={checkboxId}
                        variant="secondary"
                        checked={ignored}
                        onCheckedChange={(checked) => onToggle(checked)}
                    />
                    <label htmlFor={checkboxId} className="text-fg-secondary text-sm">
                        {checkboxLabel}
                    </label>
                </div>
                {ignored && showDuration && (
                    <DurationSelect
                        isIgnored={isIgnored}
                        blockedFrom={blockedFrom}
                        onChange={onDurationChange}
                        onPendingChange={setDurationPending}
                    />
                )}
            </div>
            {(userEdited || (ignored && durationPending)) && reasonPath && (
                <ChangeReasonInput control={control} name={reasonPath} required />
            )}
        </div>
    );
};

const MutedText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className="text-caption text-fg-secondary">{children}</span>
);

// ─── Duration select ───────────────────────────────────────────────────────────

interface DurationSelectProps {
    isIgnored: boolean;
    blockedFrom: string | null;
    onChange: (next: { isIgnored: boolean; blockedFrom: string | null }) => void;
    // Reports the "until date" mode-without-date interim state to the parent.
    // Switching to "until date" before picking a date produces no form-level
    // change (the wire shape can't represent it), so the parent needs this
    // signal to drive reason-input visibility. Goes back to false the moment
    // the user picks a date or reverts to "Indefinitely".
    onPendingChange?: (pending: boolean) => void;
}

type DurationMode = 'indefinitely' | 'until-date';

const DURATION_OPTIONS: { value: DurationMode; label: string }[] = [
    { value: 'indefinitely', label: 'Indefinitely' },
    { value: 'until-date', label: 'Until date' },
];

// "until-date" without a date yet isn't representable in the wire shape
// (isIgnored=false + blockedFrom=null reads as plain "not ignored"). We hold
// the in-between state locally until the user actually picks a date.
const DurationSelect: React.FC<DurationSelectProps> = ({ isIgnored, blockedFrom, onChange, onPendingChange }) => {
    const effectiveFormat = useEffectiveDateFormat();
    const [pendingUntilDate, setPendingUntilDateState] = useState(false);
    const mode: DurationMode = pendingUntilDate || !!blockedFrom ? 'until-date' : 'indefinitely';
    const untilDisplay = blockedFromToDisplayDate(blockedFrom);

    const setPendingUntilDate = (pending: boolean) => {
        setPendingUntilDateState(pending);
        onPendingChange?.(pending);
    };

    const handleModeChange = (newMode: DurationMode) => {
        if (newMode === 'until-date') {
            setPendingUntilDate(true);
        } else {
            setPendingUntilDate(false);
            onChange({ isIgnored: true, blockedFrom: null });
        }
    };

    const handleUntilChange = (val: Date | null) => {
        // An entry still being typed arrives as an invalid date, which has no day to store.
        if (!val || !isValid(val)) return;
        setPendingUntilDate(false);
        onChange({ isIgnored: false, blockedFrom: displayDateToBlockedFrom(val) });
    };

    // isIgnored is read by the parent to compute mode but the chip-internal
    // mode derives from blockedFrom too; this prop is kept for symmetry with
    // the override write shape.
    void isIgnored;

    return (
        <>
            <Select items={DURATION_OPTIONS} value={mode} onValueChange={(value) => value && handleModeChange(value)}>
                <SelectTrigger size="sm" className="bg-paper min-w-[130px] text-[13px] data-[size=sm]:h-7">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {DURATION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {mode === 'until-date' && (
                <DatePickerField
                    value={untilDisplay}
                    onChange={handleUntilChange}
                    minDate={new Date()}
                    format={effectiveFormat}
                    actions={['today', 'accept']}
                    clearable={false}
                    className="h-7 w-[170px] [&_input]:h-full [&_input]:text-base md:[&_input]:text-[13px]"
                />
            )}
        </>
    );
};

export default BlockingPolicyEditor;
